import React from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveTabs, SideNavTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * FTO workspace — Round HR-PA (Part 4).
 *
 * ONE place for everything Flexible Time Off, with the owner's sub-nav:
 *
 *   ADD FTO | REMOVE FTO | HOLIDAYS | APPROVALS | SUMMARY   (+ More)
 *
 *   ADD FTO   → the existing HR direct-entry form (AddFtoHr): paid or
 *               unpaid, full or half days — entered pre-approved, and the
 *               PAID balance deduction commits atomically with it.
 *   REMOVE FTO→ the existing Leave Archive locked to FTO: find any
 *               request (pending/approved/denied) and HR-void it — the
 *               approved-paid void RESTORES the balance in the same
 *               transaction (server-side finalization service).
 *   HOLIDAYS  → the existing company-holiday manager (global paid days;
 *               the per-employee exclusion feature is visible but gated
 *               until the proposed table is approved).
 *   APPROVALS → the SAME review queue everyone uses, pre-filtered to
 *               FTO — one queue, one counter, just focused.
 *   SUMMARY   → the existing FTO HR summary (balances per employee).
 *   MORE      → lower-frequency admin kept, not removed (Part 3):
 *               Paid FTO payouts, Year-end, Year-end requests,
 *               Compensation days, and the Record-absence wizard link.
 *
 * Every embedded page is the SAME component with its original endpoints,
 * permissions and validations — this file is composition only.
 * User-facing terminology: "Flexible Time Off" (never "Free Time Off");
 * DB/API names (FTORequest, /api/fto/…) are intentionally unchanged.
 */
const LazyAddFto   = React.lazy(() => import('../AddFtoHr'));
const LazyArchive  = React.lazy(() => import('../LeaveArchive'));
const LazyHolidays = React.lazy(() => import('../FtoHolidays'));
const LazyQueue    = React.lazy(() => import('../HrReviewDashboard'));
const LazySummary  = React.lazy(() => import('../FtoHrSummary'));
// Round HR-PA UI batch (2026-08-18): owner ruled the 'Paid FTO payouts'
// tool redundant — its lookup now lives inside the Add FTO page (per-emp
// and all-employees, searchable by name or empid). AddPaidFto.js stays on
// disk, unreferenced, for rollback.
const LazyYearEnd  = React.lazy(() => import('../FtoYearEnd'));
const LazyYeReq    = React.lazy(() => import('../YearEndRequestView'));
const LazyCompDays = React.lazy(() => import('../CompensationDays'));

const TABS = [
    { key: 'add',       label: 'Add FTO',    icon: 'ri-add-circle-line' },
    { key: 'remove',    label: 'Remove FTO', icon: 'ri-delete-bin-6-line' },
    { key: 'edit',      label: 'Edit FTO',   icon: 'ri-pencil-line' },   // owner 2026-11-02
    { key: 'holidays',  label: 'Holidays',   icon: 'ri-calendar-event-line' },
    { key: 'approvals', label: 'Approvals',  icon: 'ri-checkbox-multiple-line' },
    { key: 'summary',   label: 'Summary',    icon: 'ri-table-line' },
    { key: 'more',      label: 'More',       icon: 'ri-more-2-line' },
];

const MORE_ITEMS = [
    { key: 'year-end',         label: 'Year-end FTO',       icon: 'ri-refresh-line',     Comp: LazyYearEnd },
    { key: 'year-end-request', label: 'Year-end requests',  icon: 'ri-file-list-2-line', Comp: LazyYeReq },
    { key: 'comp-days',        label: 'Compensation days',  icon: 'ri-gift-line',        Comp: LazyCompDays },
];

const HrFtoWorkspace = ({ tab, moreItem, onTabChange, onMoreItemChange }) => {
    const activeTab = TABS.some((t) => t.key === tab) ? tab : 'add';
    const activeMore = MORE_ITEMS.find((i) => i.key === moreItem) || MORE_ITEMS[0];
    const MoreComp = activeMore.Comp;

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Flexible Time Off (FTO)</h5>
                    <span className="text-muted">
                        Add, remove, holidays, approvals, and balances — one workspace.
                    </span>
                </div>
            </div>

            <ResponsiveTabs
                tabs={TABS}
                activeKey={activeTab}
                onChange={onTabChange}
                variant="pills"
                size="sm"
            />

            {activeTab === 'add' && <HrEmbeddedPage><LazyAddFto /></HrEmbeddedPage>}

            {activeTab === 'remove' && (
                <div>
                    <p className="text-muted small mb-2" style={{ maxWidth: 760 }}>
                        Find the FTO entry (any status) and void it. Voiding an approved
                        <strong> paid</strong> request restores the employee&rsquo;s balance in the
                        same operation — the request and the balance can never disagree.
                    </p>
                    <HrEmbeddedPage><LazyArchive kind="fto" lockKind /></HrEmbeddedPage>
                </div>
            )}

            {activeTab === 'edit' && (
                <div>
                    <p className="text-muted small mb-2" style={{ maxWidth: 760 }}>
                        Find the FTO entry (any status) and change it in place: dates, half days,
                        Paid / Unpaid, Scheduled / Unscheduled, type. The entry keeps its number and
                        its approvals; a paid balance deduction follows the new dates. Audited, no e-mails.
                    </p>
                    <HrEmbeddedPage><LazyArchive kind="fto" lockKind action="edit" /></HrEmbeddedPage>
                </div>
            )}

            {activeTab === 'holidays' && <HrEmbeddedPage><LazyHolidays /></HrEmbeddedPage>}

            {activeTab === 'approvals' && (
                <HrEmbeddedPage><LazyQueue initialKind="FTO" /></HrEmbeddedPage>
            )}

            {activeTab === 'summary' && <HrEmbeddedPage><LazySummary /></HrEmbeddedPage>}

            {activeTab === 'more' && (
                <div>
                    <div className="text-muted small mb-2">
                        Lower-frequency FTO administration — moved here, not removed.
                        Recording a sick/absence day lives in the{' '}
                        <Link to="/humanresource?section=leave&area=record">Record absence wizard</Link>.
                    </div>
                    <div className="hr-sidegrid">
                        <SideNavTabs
                            tabs={MORE_ITEMS.map((i) => ({ key: i.key, label: i.label, icon: i.icon }))}
                            activeKey={activeMore.key}
                            onChange={onMoreItemChange}
                            width={220}
                            ariaLabel="More FTO tools"
                        />
                        <HrEmbeddedPage><MoreComp /></HrEmbeddedPage>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HrFtoWorkspace;
